import { CreditCard, Plus, ShieldCheck } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { SoftCard } from '@/components/ui/soft-card';
import { SectionHeader } from '@/components/ui/section-header';

/**
 * Payment method section. Three states:
 *   - card on file: brand + last 4 + expiry, with "Update card" CTA
 *   - no card on file but billing exists: prompt + "Add card" CTA
 *   - no Stripe customer yet: not rendered (caller decides via billing.status)
 */
export function PaymentMethodCard({ paymentMethod, hasPaymentMethod, onUpdate }) {
	const cta = hasPaymentMethod ? { label: 'Update card', variant: 'outline' } : { label: 'Add a card', variant: 'action' };

	return (
		<section>
			<SectionHeader
				eyebrow="Payment"
				title="Payment method"
				sub="Used for your monthly Unify subscription, channel add-ons, and ad spend."
			/>
			<SoftCard className="space-y-4 !p-5 border-border">
				{paymentMethod ? (
					<CardOnFile method={paymentMethod} />
				) : (
					<div className="rounded-xl border border-dashed border-border bg-muted/40 p-4">
						<div className="flex items-center gap-3">
							<div className="flex h-10 w-10 items-center justify-center rounded-xl bg-action-soft text-action-soft-foreground">
								<CreditCard className="h-5 w-5" />
							</div>
							<div className="min-w-0">
								<div className="text-sm font-semibold text-foreground">No card on file</div>
								<div className="text-xs text-muted-foreground">
									Add one to keep your subscription, channels, and ad spend running.
								</div>
							</div>
						</div>
					</div>
				)}

				<div className="flex flex-wrap items-center justify-between gap-3">
					<div className="flex items-center gap-2 text-xs text-muted-foreground">
						<ShieldCheck className="h-4 w-4" />
						Card details are stored securely with Stripe — we never see your number.
					</div>
					<Button variant={cta.variant} onClick={onUpdate}>
						{!hasPaymentMethod ? <Plus className="h-4 w-4" /> : null}
						{cta.label}
					</Button>
				</div>
			</SoftCard>
		</section>
	);
}

function CardOnFile({ method }) {
	const brand = method.brand || (method.title?.split(' •••')[0] ?? '').trim() || 'CARD';
	return (
		<div className="flex items-center gap-4 rounded-xl border border-border bg-card p-4">
			<div className="flex h-10 w-14 shrink-0 items-center justify-center rounded-lg bg-foreground/95 text-[10px] font-bold uppercase tracking-wider text-background">
				{brand}
			</div>
			<div className="min-w-0 flex-1">
				<div className="text-sm font-semibold text-foreground" data-mf-replace="**REMOVED**">
					{method.title}
				</div>
				<div className="text-xs text-muted-foreground" data-mf-replace="**REMOVED**">
					{method.description}
				</div>
			</div>
		</div>
	);
}
